import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon } from "./icon";

export interface FieldProps {
  label?: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  /** Validation error — rendered in red (danger) beneath the field, like the login page. */
  error?: ReactNode;
  /** Optional control rendered at the right edge of the label row (e.g. an "Auto select" link). */
  labelAction?: ReactNode;
  required?: boolean;
  full?: boolean;
  /**
   * Render as a plain `<div>` instead of a `<label>`. Use when the field wraps a *group* of
   * controls (e.g. a grid of option buttons) rather than a single input: a `<label>` implicitly
   * associates with its first descendant control, so hovering anywhere in the group would
   * highlight that first control as if hovered.
   */
  group?: boolean;
}

export function Field({
  label,
  children,
  hint,
  error,
  labelAction,
  required,
  full,
  group,
}: FieldProps) {
  const labelText = label && (
    <span className="field-label">
      {label}
      {required && <span className="req">*</span>}
    </span>
  );
  const Wrapper = group ? "div" : "label";
  return (
    <Wrapper className={cn("field", full && "field-full")}>
      {labelAction ? (
        <span className="field-label-row">
          {labelText}
          {labelAction}
        </span>
      ) : (
        labelText
      )}
      {children}
      {error ? (
        <span className="field-err">
          <Icon name="alert" size={13} />
          {error}
        </span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </Wrapper>
  );
}
